import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AppsTable, SectionTitle } from "@/components/AppTable";
import { toast } from "sonner";
import { Rocket, Lightning, Plus, X, ArrowClockwise, Trash } from "@phosphor-icons/react";

const MARKETS = [
  ["in", "🇮🇳 India"], ["us", "🇺🇸 US"], ["gb", "🇬🇧 UK"], ["ca", "🇨🇦 Canada"],
  ["au", "🇦🇺 Australia"], ["de", "🇩🇪 Germany"], ["fr", "🇫🇷 France"],
  ["id", "🇮🇩 Indonesia"], ["br", "🇧🇷 Brazil"],
];

export default function FreshLaunches() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hunting, setHunting] = useState(false);
  const [jobs, setJobs] = useState([]);

  const [label, setLabel] = useState("");
  const [market, setMarket] = useState("in");
  const [maxInstalls, setMaxInstalls] = useState(500000);
  const [minInstalls, setMinInstalls] = useState(0);
  const [maxDays, setMaxDays] = useState(90);
  const [minRating, setMinRating] = useState("");
  const [maxRating, setMaxRating] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [appsPerQuery, setAppsPerQuery] = useState(10);

  useEffect(() => { api.get("/meta").then(r => setAllCategories(r.data.categories || [])); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/apps-stats/fresh-launches", {
        params: { max_installs: maxInstalls, max_days: maxDays, limit: 100 },
      });
      setItems(r.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [maxInstalls, maxDays]);

  const loadJobs = async () => {
    try {
      const r = await api.get("/discovery/jobs?type=new-app-hunt&limit=15");
      setJobs(r.data);
    } catch { /* ignore for non-admin */ }
  };
  useEffect(() => {
    loadJobs();
    const t = setInterval(loadJobs, 4000);
    return () => clearInterval(t);
  }, []);

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !keywords.includes(kw)) setKeywords([...keywords, kw]);
    setKeywordInput("");
  };
  const removeKw = (k) => setKeywords(keywords.filter(x => x !== k));
  const toggleCat = (c) => setCategories(categories.includes(c) ? categories.filter(x => x !== c) : [...categories, c]);

  const hunt = async () => {
    setHunting(true);
    try {
      const body = {
        market, max_installs: maxInstalls, min_installs: minInstalls,
        max_days_since_release: maxDays, apps_per_query: appsPerQuery,
      };
      if (label.trim()) body.label = label.trim();
      if (keywords.length) body.keywords = keywords;
      if (categories.length) body.categories = categories;
      if (minRating) body.min_rating = Number(minRating);
      if (maxRating) body.max_rating = Number(maxRating);
      const r = await api.post("/discovery/new-app-hunt", body);
      toast.success(`Hunt started · ${r.data.job.label}`);
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hunt failed. Admins only.");
    } finally { setHunting(false); }
  };

  const deleteJob = async (id) => {
    try { await api.delete(`/discovery/jobs/${id}`); loadJobs(); } catch { toast.error("Failed"); }
  };

  const statusColor = (s) => s === "completed" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
    : s === "failed" ? "bg-red-500/15 text-red-600 border-red-500/30"
    : "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30";

  return (
    <div className="p-8 space-y-6">
      <SectionTitle eyebrow="Reachable publishers" title="Fresh Launches"
        actions={
          <>
            <button data-testid="btn-refresh" onClick={() => { load(); loadJobs(); }}
                    className="border border-border px-3 py-2 text-xs rounded-sm hover:bg-accent flex items-center gap-1">
              <ArrowClockwise size={12} /> Refresh
            </button>
            <button data-testid="btn-hunt" disabled={hunting} onClick={hunt}
                    className="bg-primary text-primary-foreground px-4 py-2 text-sm rounded-sm flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50">
              <Rocket size={14} /> {hunting ? "Queuing…" : "Queue Hunt"}
            </button>
          </>
        }
      />

      <div className="border border-border p-4 bg-secondary/30 space-y-4">
        <div>
          <div className="overline-label text-muted-foreground mb-1">Label (optional, helps you tell parallel hunts apart)</div>
          <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="hunt-label"
                 placeholder="e.g. India fintech 30d ≤100K"
                 className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="overline-label text-muted-foreground mb-1">Country / Market</div>
            <select data-testid="hunt-market" value={market} onChange={(e) => setMarket(e.target.value)}
                    className="w-full border border-input bg-background px-2 py-2 text-sm rounded-sm">
              {MARKETS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            </select>
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Max installs</div>
            <select data-testid="hunt-max-installs" value={maxInstalls} onChange={(e) => setMaxInstalls(Number(e.target.value))}
                    className="w-full border border-input bg-background px-2 py-2 text-sm rounded-sm">
              <option value={10000}>≤ 10K</option><option value={50000}>≤ 50K</option>
              <option value={100000}>≤ 100K</option><option value={500000}>≤ 500K</option>
              <option value={1000000}>≤ 1M</option><option value={10000000}>≤ 10M</option>
            </select>
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Min installs</div>
            <select data-testid="hunt-min-installs" value={minInstalls} onChange={(e) => setMinInstalls(Number(e.target.value))}
                    className="w-full border border-input bg-background px-2 py-2 text-sm rounded-sm">
              <option value={0}>Any</option><option value={100}>≥ 100</option>
              <option value={1000}>≥ 1K</option><option value={10000}>≥ 10K</option>
              <option value={50000}>≥ 50K</option><option value={100000}>≥ 100K</option>
            </select>
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Released within</div>
            <select data-testid="hunt-days" value={maxDays} onChange={(e) => setMaxDays(Number(e.target.value))}
                    className="w-full border border-input bg-background px-2 py-2 text-sm rounded-sm">
              <option value={7}>7 days</option><option value={30}>30 days</option>
              <option value={60}>60 days</option><option value={90}>90 days</option>
              <option value={180}>180 days</option>
            </select>
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Min rating</div>
            <input value={minRating} onChange={(e) => setMinRating(e.target.value)} placeholder="e.g. 3.5"
                   className="w-full border border-input bg-background px-2 py-2 text-sm rounded-sm" />
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Max rating</div>
            <input value={maxRating} onChange={(e) => setMaxRating(e.target.value)} placeholder="e.g. 4.5"
                   className="w-full border border-input bg-background px-2 py-2 text-sm rounded-sm" />
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Apps per query</div>
            <input type="number" min={5} max={50} value={appsPerQuery} onChange={(e) => setAppsPerQuery(Number(e.target.value))}
                   className="w-full border border-input bg-background px-2 py-2 text-sm rounded-sm" />
          </div>
        </div>

        <div>
          <div className="overline-label text-muted-foreground mb-1">Keywords</div>
          <div className="flex gap-1.5">
            <input value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                   placeholder="e.g. indie puzzle, habit tracker"
                   data-testid="hunt-keyword-input"
                   className="flex-1 border border-input bg-background px-3 py-2 text-sm rounded-sm" />
            <button type="button" onClick={addKeyword} data-testid="btn-add-keyword"
                    className="border border-border px-3 rounded-sm hover:bg-accent"><Plus size={14} /></button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {keywords.map((k) => (
              <span key={k} className="text-xs bg-primary/10 text-primary border border-primary/30 rounded-sm px-2 py-0.5 flex items-center gap-1">
                {k} <button onClick={() => removeKw(k)}><X size={10} /></button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="overline-label text-muted-foreground mb-1">Categories</div>
          <div className="flex flex-wrap gap-1">
            {allCategories.slice(0, 24).map((c) => (
              <button key={c} type="button" onClick={() => toggleCat(c)}
                      className={`text-[11px] px-2 py-0.5 rounded-sm border ${categories.includes(c) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Lightning size={12} /> Each hunt runs in the background — you can queue several with different filters at once.
        </div>
      </div>

      {jobs.length > 0 && (
        <section>
          <div className="overline-label text-muted-foreground mb-2">Hunt Jobs</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {jobs.map((j) => {
              const scanned = j.scanned ?? j.total_scanned ?? 0;
              const total = j.params?.queries_per_run ? j.params.queries_per_run * (j.params.apps_per_query || 10) : 200;
              const pct = j.status === "completed" ? 100 : Math.min(99, Math.floor((scanned / Math.max(1, total)) * 100));
              return (
                <div key={j.id} data-testid={`hunt-job-${j.id}`}
                     className="border border-border bg-card p-3 rounded-sm">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{j.label || `${(j.market || "").toUpperCase()} hunt`}</div>
                      <div className="text-[10px] text-muted-foreground font-mono truncate">
                        {j.market} · ≤{j.params?.max_installs || "?"} installs · {j.params?.max_days_since_release || "?"}d
                        {j.current_query && ` · scanning "${j.current_query}"`}
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 border rounded-sm font-mono ${statusColor(j.status)}`}>{j.status}</span>
                    <button onClick={() => deleteJob(j.id)}
                            className="p-1 hover:bg-destructive/10 hover:text-destructive rounded-sm">
                      <Trash size={12} />
                    </button>
                  </div>
                  <div className="w-full bg-secondary h-1 rounded-sm overflow-hidden mt-2">
                    <div className={`h-full transition-all ${j.status === "completed" ? "bg-emerald-500" : j.status === "failed" ? "bg-red-500" : "bg-primary"}`}
                         style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-[10px] font-mono text-muted-foreground">
                    <span>scanned {scanned}</span>
                    <span>kept {j.results ?? 0}</span>
                  </div>
                  {j.error && <div className="text-[10px] text-red-600 mt-1">{j.error}</div>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <AppsTable apps={items} />
    </div>
  );
}
